<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute, useRouter, type RouteRecordName } from 'vue-router';

import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';

import HeaderMobile from './app-header/HeaderMobile.vue';
import useNavigationLinks from './app-header/useNavigationLink';
import InternalNavigation from './InternalNavigation.vue';
import PageWithSideBar from './PageWithSideBar.vue';

const route = useRoute();
const router = useRouter();
const { isXS } = useMQBreakpoints();
const { navLinks } = useNavigationLinks();

const showLeftNav = computed(() => !isXS.value);

const links = computed(() => {
  const { settings, levels, partners, exports, help, blog, legal } = navLinks.value;

  return {
    primary: [settings, levels, partners, exports],
    secondary: [help, blog, legal],
  };
});

const title = computed(() => {
  const getMainName = (name?: string | RouteRecordName | null) => {
    if (!name || typeof name === 'symbol') {
      return '';
    }

    return name.split('.')[0] || '';
  };
  const mainRouteName = getMainName(route.name);

  return links.value.primary.find((link) => getMainName(link.route?.name) === mainRouteName)?.pageTitle;
});

const onMobileGoBack = () => {
  router.push({
    name: 'mobile-profile',
  });
};
</script>

<template>
  <page-with-side-bar
    class="page-with-side-bar-internal-navigation"
    :show-left-nav="showLeftNav"
  >
    <template #leftNav>
      <internal-navigation
        class="page-with-side-bar-internal-navigation__left-nav"
        :links="links"
      />
    </template>
    <template #default>
      <template v-if="title">
        <div
          v-if="showLeftNav"
          class="page-with-side-bar-internal-navigation__title"
        >
          {{ title }}
        </div>
        <header-mobile
          v-else
          class="page-with-side-bar-internal-navigation__header"
          @go-back="onMobileGoBack"
        >
          <template #top-content>
            <div class="page-with-side-bar-internal-navigation__title page-with-side-bar-internal-navigation__title--narrow">
              {{ title }}
            </div>
          </template>
        </header-mobile>
      </template>
      <router-view
        v-slot="{ Component }"
        name="default"
      >
        <component
          :is="Component"
          class="page-with-side-bar-internal-navigation__section-main"
        ></component>
      </router-view>
    </template>
  </page-with-side-bar>
</template>

<style lang="scss">
.page-with-side-bar-internal-navigation {
  &__left-nav {
    padding: 0 16px;
  }

  &__title {
    flex: 0 0 auto;
    margin: 24px 0;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-xxl);
    font-weight: var(--font-weight-bold);
    letter-spacing: normal;
    text-transform: uppercase;

    &--narrow {
      margin: 0;
    }
  }

  &__header {
    flex: 0 0 auto;
    margin: 0 calc(-1 * var(--padding-x));

    .xs & {
      margin: 0 calc(-1 * var(--padding-x-xs));
    }
  }

  &__section-main {
    flex: 1 0 auto;
    padding-top: 24px;
    border-top: 3px solid rgb(var(--v-theme-elevation-1));

    .xs &,
    .sm & {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
    }
  }
}
</style>
